import referralApi from '@/api/referral'
import {
  loadReditusScript, initReditusWidget, showReditusWidget, isMockMode,
} from '@/services/loaderPlugin/reditus.loader'
import * as Sentry from '@sentry/vue'

const state = {
  widgetLoaded: false,
  hasUnreadReward: false,
  mockMode: false,
  showMockModal: false,
}

const _getters = {
  isWidgetReady: (s) => s.widgetLoaded,
  hasUnreadReward: (s) => s.hasUnreadReward,
  isMockMode: (s) => s.mockMode,
  showMockModal: (s) => s.showMockModal,
}

/* eslint-disable no-param-reassign */
const _mutations = {
  SET_WIDGET_LOADED(s, val) { s.widgetLoaded = val },
  SET_HAS_UNREAD_REWARD(s, val) { s.hasUnreadReward = val },
  SET_MOCK_MODE(s, val) { s.mockMode = val },
  SET_SHOW_MOCK_MODAL(s, val) { s.showMockModal = val },
}
/* eslint-enable no-param-reassign */

const _actions = {
  async initWidget({ commit, rootState }) {
    try {
      referralApi.initResource()
      const tokenData = await referralApi.getToken()

      if (tokenData.mock) {
        commit('SET_MOCK_MODE', true)
        commit('SET_WIDGET_LOADED', true)
        commit('SET_HAS_UNREAD_REWARD', true)
        return
      }

      await loadReditusScript()

      const user = rootState.currentUser
      initReditusWidget(tokenData.productId, tokenData.token, {
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
      })

      commit('SET_MOCK_MODE', isMockMode())
      commit('SET_WIDGET_LOADED', true)
    } catch (err) {
      // API unavailable or token fetch failed — fall back to mock mode
      // so the UI still works (shows mock modal on click)
      console.warn('Referral widget init failed, falling back to mock mode:', err.message)
      Sentry.captureException(err)
      commit('SET_MOCK_MODE', true)
      commit('SET_WIDGET_LOADED', true)
    }
  },

  async fetchRewardStatus({ commit, state: s }) {
    if (s.mockMode) {
      commit('SET_HAS_UNREAD_REWARD', true)
      return
    }
    try {
      const data = await referralApi.getRewardStatus()
      commit('SET_HAS_UNREAD_REWARD', data.hasUnreadReward)
    } catch (err) {
      Sentry.captureException(err)
    }
  },

  async acknowledgeReward({ commit, state: s }) {
    commit('SET_HAS_UNREAD_REWARD', false)
    if (!s.mockMode) {
      try {
        await referralApi.acknowledgeReward()
      } catch (err) {
        Sentry.captureException(err)
      }
    }
  },

  openWidget({ commit, dispatch, state: s }) {
    if (s.mockMode) {
      commit('SET_SHOW_MOCK_MODAL', true)
    } else {
      showReditusWidget()
    }
    if (s.hasUnreadReward) {
      dispatch('acknowledgeReward')
    }

    // Notify BE that the widget was opened so it can run its retry chain
    // to sync the advocate slug from Reditus. Fire-and-forget — the chain
    // runs entirely on the server and survives the user closing the app.
    if (!s.mockMode) {
      referralApi.notifyWidgetOpened().catch((err) => Sentry.captureException(err))
    }
  },

  closeMockModal({ commit }) {
    commit('SET_SHOW_MOCK_MODAL', false)
  },
}

export default {
  namespaced: true,
  state,
  getters: _getters,
  mutations: _mutations,
  actions: _actions,
}
